import React, { useEffect, useState } from "react";
import { FaFileExcel } from "react-icons/fa";
import * as XLSX from 'xlsx';
import { getAllUsersAPI } from "../../../utils/api";
import "./WholesellerReport.css";

const WholesellerReport = ({ showToast }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  console.log("users", users);

  const [reportFilters, setReportFilters] = useState({
    status: ""
  });

  const handleReportFilterChange = (field, value) => {
    setReportFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearReportFilters = () => {
    setReportFilters({
      status: ""
    });
    fetchReportData({
      status: ""
    });
  };

  const fetchReportData = async (filters) => {
    try {
      setLoading(true);
      setError(null);

      let requestBody = {
        user_type: "wholeseller",
        is_report: 1
      };

      if (filters.status !== "") requestBody.active = filters.status === "1" ? 1 : 0;

      const response = await getAllUsersAPI(requestBody);

      if (response?.status === 200) {
        setUsers(response?.data || []);
      } else if (response?.message) {
        setError(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error');
      } else {
        setError('Failed due to internal error');
      }
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError('Failed due to internal error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit=async()=>{
    try {
      setLoading(true);

      let requestBody = {
        user_type: "wholeseller",
        is_report: 1
      };

      if (reportFilters.status !== "") requestBody.active = reportFilters.status === "1" ? 1 : 0;

      const response = await getAllUsersAPI(requestBody);

      if (response?.status === 200) {
        const reportUsers = response?.data || [];
        setUsers(reportUsers);
      } else {
        showToast("Failed to fetch data for Excel generation", "error");
      }
    } catch (err) {
      console.error('Error generating Excel:', err);
      showToast("Failed to generate Excel file", "error");
    } finally {
      setLoading(false);
    }
  }

  const generateExcel = async () => {
    try {
      setLoading(true);

      let requestBody = {
        user_type: "wholeseller",
        is_report: 1
      };

      if (reportFilters.status !== "") requestBody.active = reportFilters.status === "1" ? 1 : 0;

      const response = await getAllUsersAPI(requestBody);

      if (response?.status === 200) {
        const reportUsers = response?.data || [];
        setUsers(reportUsers);

        if (reportUsers.length === 0) {
          showToast("No data available to generate Excel", "error");
          return;
        }

        // Prepare data for Excel with left-aligned Sr. No.
        const excelData = reportUsers.map((user, index) => ({
          'Sr. No.': ` ${index + 1}`, // Add space prefix to force left alignment
          'Name': user.name || '---',
          'Email': user.email || '---',
          'Country': user.country || '---',
          'Phone': `${user.country_code || ''} ${user.phoneno || ''}`.trim() || '---',
          'Passport Number': user.passport_no || '---',
          'PAN Card Number': user.pancard_no || '---',
          'GST Number': user.gst_no || '---',
          'Status': user.active ? 'Active' : 'Inactive'
        }));

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Set column widths
        const columnWidths = [
          { wch: 8 },   // Sr. No.
          { wch: 25 },  // Name
          { wch: 35 },  // Email
          { wch: 20 },  // Country
          { wch: 20 },  // Phone
          { wch: 35 },  // Passport Number
          { wch: 35 },  // PAN Card Number
          { wch: 35 },  // GST Number (increased width)
          { wch: 10 }   // Status
        ];
        worksheet['!cols'] = columnWidths;

        // Set alignment for Sr. No. column (left align)
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        for (let row = range.s.r; row <= range.e.r; row++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: 0 }); // Column A (Sr. No.)
          if (worksheet[cellAddress]) {
            if (!worksheet[cellAddress].s) worksheet[cellAddress].s = {};
            worksheet[cellAddress].s.alignment = { horizontal: 'left' };
          }
        }

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Wholeseller Report');

        // Generate Excel file with styling
        const fileName = `wholeseller-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName, {
          bookType: 'xlsx',
          bookSST: false,
          type: 'binary'
        });

        showToast("Excel file generated and downloaded successfully", "success");
      } else {
        showToast("Failed to fetch data for Excel generation", "error");
      }
    } catch (err) {
      console.error('Error generating Excel:', err);
      showToast("Failed to generate Excel file", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData({
      status: ""
    });
  }, []);

  return (
    <div className="wholeseller-report">
      {/* Report Filter Section */}
      <div className="report-filter-row">
        <div className="report-filter-group">
          <label>Status</label>
          <select
            value={reportFilters.status}
            onChange={(e) => handleReportFilterChange('status', e.target.value)}
            className="report-filter-select"
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>
        <div className="report-filter-actions">
          <button className="report-clear-btn" onClick={clearReportFilters}>
            Clear
          </button>
          <button className="report-generate-btn" onClick={onSubmit}>
            Submit
          </button>
          <button className="report-generate-btn" onClick={generateExcel}>
            <FaFileExcel />
            Generate Excel
          </button>
        </div>
      </div>

      {loading && (
        <div className="report-loading-container">
          <div className="report-loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      )}

      {error && (
        <div className="report-error-container">
          <div style={{ color: "#dc2626", fontSize: "1.25rem", marginBottom: "1rem" }}>⚠️</div>
          <p style={{ color: "#dc2626", fontSize: "1.125rem" }}>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="report-summary-cards">
          <div className="summary-card">
            <h3>Total Users</h3>
            <p>{users.length}</p>
          </div>
          <div className="summary-card">
            <h3>Active Users</h3>
            <p>{users.filter(user => user.active).length}</p>
          </div>
          <div className="summary-card">
            <h3>Inactive Users</h3>
            <p>{users.filter(user => !user.active).length}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesellerReport;
